import GausiumMapGeoPinsOnMap, {
  GausiumMapGeoPin,
} from '../Facility/FacilityView/GausiumMapGeoPinsOnMap/GausiumMapGeoPinsOnMap'
import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { IconButton } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { type GausiumExecutableTaskRecent, RobotApi } from 'api/RobotApi'
import { MainService } from 'api/schema'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import FloorMapPreviewOverlay from 'components/FloorMapPreviewOverlay'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import ScenarioTable from 'components/ScenarioTable/ScenarioTable'
import { IScenarioInfo } from 'components/SharedScenarioItem/SharedScenarioItem'
import TablePagination from 'components/TablePagination/TablePagination'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { Feature } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import type { DialogTarget, PickerScope } from 'pages/TaskScheduler/TaskSettingsModal/DialogContext'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { GAUSIUM_ENABLED, PATH, PROGRESS_TYPES } from 'utils/constants'
import { enrichGausiumExecTasksWithMapPreviews } from 'utils/gausiumScenarioFromExecTask'
import { IFacility, IMapboxElement, IMission } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'
import { SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX, SCENARIO_MAP_COLUMN_INSET_LEFT_PX } from 'utils/scenarioMapLayout'
import { enumParamUpper, optionalIntParam, pageParam, stringParam } from 'utils/urlQueryState'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './ScenarioList.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'
import mapPageFilterDropdown from 'styles/mapPageFilterDropdown.module.css'
import tablePagination from 'styles/tablePagination.module.css'

async function loadAllGausiumExecTasksAsScenarios(search?: string): Promise<IScenarioInfo[]> {
  const robotList = await MainService.mainRobotList(undefined, 1, 500, undefined, undefined, VENDOR.GAUSIUM)
  const robots = robotList.results || []
  const allRaw = (await Promise.all(robots.map(r => RobotApi.fetchGausiumExecutableTasksRecent(r.id)))).flat()
  const q = (search || '').trim().toLowerCase()
  const filtered = q
    ? allRaw.filter(
        t =>
          String(t.task_name || '')
            .toLowerCase()
            .includes(q) ||
          String(t.map_name || '')
            .toLowerCase()
            .includes(q) ||
          String(t.robot_sn || '')
            .toLowerCase()
            .includes(q)
      )
    : allRaw
  return enrichGausiumExecTasksWithMapPreviews(filtered)
}

/** Autonomics first, then Gausium maps; global indices [0, autoCount) and [autoCount, …). */
async function fetchMergedAutonomicsAndGausiumPage(
  page: number,
  pageSize: number,
  search: string | undefined
): Promise<{ merged: any[]; count: number }> {
  const [autoProbe, gsScenariosAll] = await Promise.all([
    MainApi.fetchScenarios(1, 1, search),
    loadAllGausiumExecTasksAsScenarios(search),
  ])
  const autoCount = autoProbe.count
  const gsTotal = gsScenariosAll.length
  const totalCombined = autoCount + gsTotal
  const start = (page - 1) * pageSize
  const end = Math.min(start + pageSize, totalCombined)

  if (start >= totalCombined) {
    return { merged: [], count: totalCombined }
  }

  const merged: any[] = []
  let pos = start

  while (merged.length < end - start && pos < totalCombined) {
    const chunk = end - pos
    if (pos < autoCount) {
      const ap = Math.floor(pos / pageSize) + 1
      const res = await MainApi.fetchScenarios(ap, pageSize, search)
      const off = pos % pageSize
      const takeCount = Math.min(chunk, res.results.length - off, autoCount - pos)
      if (takeCount <= 0) break
      merged.push(...res.results.slice(off, off + takeCount))
      pos += takeCount
    } else {
      const gsPos = pos - autoCount
      const takeCount = Math.min(chunk, gsTotal - gsPos)
      if (takeCount <= 0) break
      merged.push(...gsScenariosAll.slice(gsPos, gsPos + takeCount))
      pos += takeCount
    }
  }

  return { merged, count: totalCombined }
}

const ScenarioList = () => {
  const navigate = useNavigate()
  const [url, setUrl] = useUrlQueryState({
    vendor: enumParamUpper(['AUTONOMICS', 'GAUSIUM', 'ALL'] as const, 'AUTONOMICS'),
    q: stringParam(),
    page: pageParam(),
    scenario: optionalIntParam(),
  })
  const [scenarios, setScenarios] = useState<any[]>([])
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [listRefreshing, setListRefreshing] = useState(false)
  const loadingProgressRef = useRef(PROGRESS_TYPES.IDLE)
  const [creatingProgress, setCreatingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [pageInput, setPageInput] = useState(String(url.page))
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(url.q)
  const [totalCount, setTotalCount] = useState(0)
  const lastFetchedPage = useRef<string | null>(null)
  /** Monotonic id so a slower in-flight fetch cannot overwrite state after vendor/page/search changed. */
  const fetchSeqRef = useRef(0)
  const skipPageResetRef = useRef(true)
  const PAGE_SIZE = 20
  const vendorFilter = url.vendor
  const setVendorFilter = (vendor: 'AUTONOMICS' | 'GAUSIUM' | 'ALL') => setUrl({ vendor, page: 1 })
  const currentPage = url.page
  const setCurrentPage = (page: number | ((prev: number) => number)) =>
    setUrl(s => ({ page: typeof page === 'function' ? page(s.page) : page }))
  const searchQuery = url.q
  const setSearchQuery = (q: string) => setUrl({ q })
  const highlightedScenarioId = url.scenario
  const setHighlightedScenarioId = (id: number | undefined) => setUrl({ scenario: id })

  const [createDialog, setCreateDialog] = useState(false)
  const [createName, setCreateName] = useState('')
  const [facilities, setFacilities] = useState<IFacility[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | undefined>(undefined)
  const [selectedFacilityOption, setSelectedFacilityOption] = useState<IOption | undefined>(undefined)

  useEffect(() => {
    if (createDialog) {
      MainApi.fetchFacilities(1, 1000)
        .then(res => setFacilities(res.results || []))
        .catch(console.error)
    }
  }, [createDialog])

  const facilityOptions: IOption[] = facilities.map(f => ({
    text: f.name || 'Unnamed Facility',
    value: f.id,
  }))

  const onFacilitySelected = (e: IOption) => {
    setSelectedFacilityOption(e)
    setSelectedFacilityId(e.value as number)
  }

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false)
  const [launchItem, setLaunchItem] = useState<{ scenario?: any } | undefined>(undefined)
  const [gausiumLaunchMapId, setGausiumLaunchMapId] = useState<number | null>(null)
  const [gausiumLaunchExecTask, setGausiumLaunchExecTask] = useState<GausiumExecutableTaskRecent | null>(null)

  // Map state
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const [selectedMissionsDetails, setSelectedMissionsDetails] = useState<IMission[]>([])
  const [gausiumMapGeoPins, setGausiumMapGeoPins] = useState<GausiumMapGeoPin[]>([])
  const lastGausiumCameraFocusScenarioId = useRef<number | undefined>(undefined)

  const isMobile = useIsMobile()
  const [showTable, setShowTable] = useState(true)

  loadingProgressRef.current = loadingProgress

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  const handleScenarioSelect = useCallback(
    (id: number) => {
      // Re-click on the same row should re-open GS preview after manual reset/close.
      if (highlightedScenarioId === id) {
        setGausiumPreviewDismissed(false)
        return
      }
      setHighlightedScenarioId(id)
      setGausiumPreviewDismissed(false)
    },
    [highlightedScenarioId]
  )

  // Fetch full mission geodata for all missions in the selected scenario
  useEffect(() => {
    if (!highlightedScenarioId) {
      setSelectedMissionsDetails([])
      return
    }
    const scenario = scenarios.find(s => s.id === highlightedScenarioId)
    if (scenario?.is_gausium_map || scenario?.is_gausium_executable_task) {
      setSelectedMissionsDetails([])
      return
    }
    if (!scenario || !scenario.missions_ids?.length) {
      setSelectedMissionsDetails([])
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const missionIds = (scenario.missions_ids || []).filter((id: any) => typeof id === 'number')
        const results = await Promise.all(missionIds.map((id: number) => MainApi.fetchMission(id)))
        if (!cancelled) setSelectedMissionsDetails(results)
      } catch (error) {
        console.error('Failed to fetch mission details for scenario', error)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [highlightedScenarioId, scenarios])

  const gausiumMapPinHighlightId = useMemo(() => {
    if (highlightedScenarioId == null) return undefined
    const row = scenarios.find(s => s.id === highlightedScenarioId)
    if (row?.is_gausium_executable_task && row.gausium_map_pk != null) return row.gausium_map_pk
    return highlightedScenarioId
  }, [highlightedScenarioId, scenarios])

  const selectedGausiumPreview = useMemo(() => {
    if (!highlightedScenarioId) return null
    const s = scenarios.find(x => x.id === highlightedScenarioId)
    if (s?.is_gausium_executable_task) {
      return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
    }
    if (!s?.is_gausium_map) return null
    return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
  }, [highlightedScenarioId, scenarios])

  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [highlightedScenarioId])

  useEffect(() => {
    // GS maps: focus camera on selected map pin (point). Avoid zooming too close.
    if (!GAUSIUM_ENABLED || vendorFilter === 'AUTONOMICS') {
      lastGausiumCameraFocusScenarioId.current = undefined
      return
    }
    if (!map) return
    if (highlightedScenarioId == null) {
      lastGausiumCameraFocusScenarioId.current = undefined
      return
    }
    if (lastGausiumCameraFocusScenarioId.current === highlightedScenarioId) return

    const pin = gausiumMapGeoPins.find(p => Number(p.id) === Number(gausiumMapPinHighlightId))
    const gp = pin?.geo_point
    if (gp?.type !== 'Point' || !gp.coordinates || gp.coordinates.length < 2) return
    const [lng, lat] = gp.coordinates as [number, number]
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return

    lastGausiumCameraFocusScenarioId.current = highlightedScenarioId
    // GS pin: ближе чем default 19 — как раньше (maxZoom 17), через pointZoom в API production mapUtils
    zoomMapTo(
      map,
      [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        },
      ],
      SCENARIO_MAP_COLUMN_INSET_LEFT_PX,
      17
    )
  }, [map, vendorFilter, highlightedScenarioId, gausiumMapGeoPins, gausiumMapPinHighlightId])

  useEffect(() => {
    // GS maps list is paginated; when user selects a map (via pin or other UI) that isn't on the current page,
    // automatically navigate to the page containing that map so preview and row highlight work.
    if (!GAUSIUM_ENABLED) return
    if (vendorFilter !== 'GAUSIUM' && vendorFilter !== 'ALL') return
    if (!highlightedScenarioId) return
    if (scenarios.some(s => s?.id === highlightedScenarioId)) return

    let cancelled = false

    const findPage = async () => {
      const all = await loadAllGausiumExecTasksAsScenarios(debouncedSearchQuery || undefined)
      if (cancelled) return
      const idx = all.findIndex(s => Number(s.id) === Number(highlightedScenarioId))
      if (idx >= 0) setCurrentPage(Math.floor(idx / PAGE_SIZE) + 1)
    }

    findPage().catch(err => {
      console.warn('[ScenarioList] auto-paginate error:', err)
    })
    return () => {
      cancelled = true
    }
  }, [vendorFilter, highlightedScenarioId, scenarios, totalCount, currentPage, debouncedSearchQuery])

  // Zoom to all mission geometry of the selected scenario
  useEffect(() => {
    if (map && selectedMissionsDetails.length > 0) {
      const features: Feature[] = selectedMissionsDetails.flatMap(m => [
        ...m.cleaningZones,
        ...m.restrictZones,
        ...m.cleaningRoutes,
        ...m.transferRoutes,
        ...(m.pointRoutes || []),
      ])
      if (features.length > 0) zoomMapTo(map, features, 580)
    }
  }, [map, selectedMissionsDetails])

  useEffect(() => {
    if (!GAUSIUM_ENABLED || vendorFilter === 'AUTONOMICS') {
      setGausiumMapGeoPins([])
      return
    }

    let cancelled = false

    const run = async () => {
      const pageSize = 200
      let page = 1
      let total = 0
      const allMaps: any[] = []

      do {
        const { results, total: currentTotal } = await MainApi.fetchGausiumMaps(undefined, undefined, page, pageSize)
        total = currentTotal || 0
        allMaps.push(...results)
        page += 1
      } while (allMaps.length < total)

      if (cancelled) return

      setGausiumMapGeoPins(
        allMaps
          .filter(
            m =>
              m?.geo_point?.type === 'Point' &&
              Array.isArray(m?.geo_point?.coordinates) &&
              m.geo_point.coordinates.length === 2
          )
          .map(m => ({
            id: m.id,
            name: m.map_name || '',
            geo_point: m.geo_point ?? null,
          }))
      )
    }

    run().catch(() => {
      if (!cancelled) setGausiumMapGeoPins([])
    })

    return () => {
      cancelled = true
    }
  }, [vendorFilter])

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      const cacheKey = `${vendorFilter}_${currentPage}_${debouncedSearchQuery}`
      if (!forceRefresh && lastFetchedPage.current === cacheKey) return
      lastFetchedPage.current = cacheKey
      const seq = ++fetchSeqRef.current
      try {
        if (loadingProgressRef.current === PROGRESS_TYPES.SUCCESS) {
          setListRefreshing(true)
        } else {
          setLoadingProgress(PROGRESS_TYPES.WORK)
        }
        let merged: any[] = []
        let count = 0

        if (GAUSIUM_ENABLED && vendorFilter === 'ALL') {
          const pack = await fetchMergedAutonomicsAndGausiumPage(
            currentPage,
            PAGE_SIZE,
            debouncedSearchQuery || undefined
          )
          merged = pack.merged
          count = pack.count
        } else if (vendorFilter === 'AUTONOMICS' || (vendorFilter === 'ALL' && !GAUSIUM_ENABLED)) {
          const auto = await MainApi.fetchScenarios(currentPage, PAGE_SIZE, debouncedSearchQuery || undefined)
          merged.push(...auto.results)
          count = Math.max(count, auto.count)
        } else if (GAUSIUM_ENABLED && vendorFilter === 'GAUSIUM') {
          const gsAll = await loadAllGausiumExecTasksAsScenarios(debouncedSearchQuery || undefined)
          const start = (currentPage - 1) * PAGE_SIZE
          merged.push(...gsAll.slice(start, start + PAGE_SIZE))
          count = Math.max(count, gsAll.length)
        }

        if (seq !== fetchSeqRef.current) return

        setScenarios(merged)
        setTotalCount(count)
        setListRefreshing(false)
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        if (seq !== fetchSeqRef.current) return
        setListRefreshing(false)
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          setLoadingProgress(PROGRESS_TYPES.IDLE)
          return
        }
        setLoadingProgress(PROGRESS_TYPES.ERROR)
        console.warn('[ScenarioList] fetchData error:', error)
      }
    },
    [currentPage, debouncedSearchQuery, vendorFilter]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])
  useEffect(() => {
    if (skipPageResetRef.current) return
    if (currentPage !== 1) setCurrentPage(1)
  }, [debouncedSearchQuery])
  useEffect(() => {
    if (skipPageResetRef.current) return
    if (currentPage !== 1) setCurrentPage(1)
  }, [vendorFilter])
  useEffect(() => {
    skipPageResetRef.current = false
  }, [])
  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const vendorScenarioOptions: IOption[] = useMemo(
    () => [
      { value: 'ALL', text: 'All vendors' },
      { value: 'AUTONOMICS', text: 'Autonomics' },
      ...(GAUSIUM_ENABLED ? [{ value: 'GAUSIUM', text: 'Gausium' }] : []),
    ],
    []
  )

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) setCurrentPage(val)
      else setPageInput(currentPage.toString())
    }
  }

  const createScenario = async () => {
    try {
      setCreatingProgress(PROGRESS_TYPES.WORK)
      const scenario = await MainApi.createScenario(createName.trim(), selectedFacilityId)
      setCreatingProgress(PROGRESS_TYPES.SUCCESS)
      setCreateDialog(false)
      setCreateName('')
      setSelectedFacilityId(undefined)
      setSelectedFacilityOption(undefined)
      navigate(`${PATH.SCENARIOS}${scenario.id}`)
    } catch (error) {
      setCreatingProgress(PROGRESS_TYPES.ERROR)
      console.error('Error creating scenario:', error)
    }
  }

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <Title text="Scenarios" />
            <Button.Standart
              className={style.button}
              onClick={() => {
                setCreateName('')
                setSelectedFacilityId(undefined)
                setSelectedFacilityOption(undefined)
                setCreateDialog(true)
              }}
              loading={creatingProgress === PROGRESS_TYPES.WORK}
            >
              New Scenario
            </Button.Standart>
          </div>
          <div className={style.controlsBlock}>
            <Dropdown
              className={style.mobilizedDropdown}
              minWidth={isMobile ? 120 : 160}
              useSmallDropdownStyle={isMobile}
              currentOption={
                vendorFilter === 'ALL'
                  ? undefined
                  : {
                      value: vendorFilter,
                      text: vendorFilter === 'AUTONOMICS' ? 'Autonomics' : 'Gausium',
                    }
              }
              placeholder="All vendors"
              options={vendorScenarioOptions}
              onClickOption={(opt: IOption) => setVendorFilter(opt.value as 'AUTONOMICS' | 'GAUSIUM' | 'ALL')}
            />
            <input
              className={style.searchInput}
              type="text"
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={style.contentContainer}>
          <Box className={clsx(style.box, { [style.boxHidden]: !showTable })}>
            <div className={style.tableWrapper}>
              {loadingProgress === PROGRESS_TYPES.WORK && scenarios.length === 0 ? (
                <Loader />
              ) : loadingProgress === PROGRESS_TYPES.ERROR ? (
                <div>Error loading scenarios</div>
              ) : (
                <>
                  {listRefreshing && (
                    <div className={style.tableLoadingOverlay} aria-busy="true" aria-live="polite">
                      <Loader />
                    </div>
                  )}
                  <ScenarioTable
                    scenarios={scenarios}
                    highlightedScenarioId={highlightedScenarioId}
                    onScenarioClick={id => handleScenarioSelect(id)}
                    onLaunchScenario={scenario => {
                      if (scenario.is_gausium_executable_task && scenario.gausium_exec_task) {
                        setGausiumLaunchMapId(null)
                        setGausiumLaunchExecTask(scenario.gausium_exec_task)
                        return
                      }
                      if (scenario.is_gausium_map) {
                        setGausiumLaunchExecTask(null)
                        setGausiumLaunchMapId(scenario.id)
                        return
                      }
                      setLaunchItem({ scenario })
                      setIsLaunchModalOpen(true)
                    }}
                    onScenariosUpdated={() => fetchData(true)}
                    hideIdColumn={isMobile || vendorFilter === 'GAUSIUM'}
                    hideRobotColumn={isMobile}
                    robotColumnTitle={vendorFilter === 'GAUSIUM' ? 'Robot' : 'Robot type'}
                  />
                </>
              )}
              {loadingProgress !== PROGRESS_TYPES.WORK && (
                <TablePagination
                  page={currentPage}
                  totalPages={Math.ceil(totalCount / PAGE_SIZE) || 1}
                  pageInput={pageInput}
                  onPageInputChange={setPageInput}
                  onPageInputKeyDown={handlePageJump}
                  onPageInputBlur={() => setPageInput(currentPage.toString())}
                  onPrev={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  onNext={() => setCurrentPage(prev => prev + 1)}
                />
              )}
            </div>
          </Box>

          <div className={style.mapWrapper} ref={mapWrapperRef}>
            <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited}>
              {GAUSIUM_ENABLED && (vendorFilter === 'GAUSIUM' || vendorFilter === 'ALL') && (
                <GausiumMapGeoPinsOnMap
                  map={map}
                  pins={gausiumMapGeoPins}
                  highlightedId={gausiumMapPinHighlightId}
                  onPinClick={mapPinId => {
                    const taskRow = scenarios.find(
                      s => s.is_gausium_executable_task && Number(s.gausium_map_pk) === Number(mapPinId)
                    )
                    if (taskRow) handleScenarioSelect(taskRow.id)
                    else handleScenarioSelect(mapPinId)
                  }}
                />
              )}
              {selectedMissionsDetails.length > 0 && (
                <>
                  <RestrictZonesOnMap restrictZones={selectedMissionsDetails.flatMap(m => m.restrictZones)} />
                  <CleaningZonesOnMap cleaningZones={selectedMissionsDetails.flatMap(m => m.cleaningZones)} />
                  <CleaningRoutesOnMap cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)} />
                  <TransferRoutesOnMap
                    transferRoutes={selectedMissionsDetails.flatMap(m => [
                      ...m.transferRoutes,
                      ...(m.pointRoutes || []),
                    ])}
                  />
                  {selectedMissionsDetails.map(m => (
                    <CleaningPointsOnMap
                      key={'pts-' + m.mission.id}
                      cleaningRoutes={m.cleaningRoutes}
                      launchPoints={m.launchPoints}
                    />
                  ))}
                  <MissionStartFinishPoints
                    startPoints={selectedMissionsDetails.map(m => m.startingPoint)}
                    finishPoints={selectedMissionsDetails.map(m => m.finishingPoint)}
                  />
                </>
              )}
            </MapboxCommon>
            <FloorMapPreviewOverlay
              visible={!!selectedGausiumPreview && !gausiumPreviewDismissed}
              previewUrl={selectedGausiumPreview?.url}
              mapName={selectedGausiumPreview?.name}
              mapWidgetRootRef={mapWrapperRef}
              insetLeftPx={SCENARIO_MAP_COLUMN_INSET_LEFT_PX}
              closeOffsetTopPx={SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX}
              onClose={() => setHighlightedScenarioId(undefined)}
            />
          </div>
        </div>
        {isMobile && (
          <IconButton
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowTable(prev => !prev)}
            title={showTable ? 'Switch to Map' : 'Switch to Table'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
              zIndex: 30,
            }}
          >
            {showTable ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </IconButton>
        )}
      </div>

      <ConfirmDialog
        title="Create a new scenario"
        isOpen={createDialog}
        onClose={() => {
          setCreateDialog(false)
          setSelectedFacilityId(undefined)
          setSelectedFacilityOption(undefined)
        }}
        onConfirmClick={createScenario}
        confirmText="Create"
        confirmButtonDisabled={!createName.trim() || !selectedFacilityId}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextFieldDescribed
            description={undefined}
            placeholder="Scenario name"
            value={createName}
            onChange={e => setCreateName(e.target.value)}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500 }}>Facility</label>
            <Dropdown
              currentOption={selectedFacilityOption}
              placeholder="Select facility"
              options={facilityOptions}
              onClickOption={onFacilitySelected}
            />
          </div>
        </div>
      </ConfirmDialog>

      {(() => {
        const isGs = gausiumLaunchMapId != null || gausiumLaunchExecTask != null
        const preselectedData =
          gausiumLaunchExecTask != null
            ? { gausiumExecutableTask: gausiumLaunchExecTask, robotId: gausiumLaunchExecTask.robot_id }
            : gausiumLaunchMapId != null
            ? { gausiumMapPk: gausiumLaunchMapId }
            : launchItem?.scenario
            ? { scenario: launchItem.scenario }
            : undefined
        const pickerScope: PickerScope | undefined = isGs ? { vendor: VENDOR.GAUSIUM } : undefined
        const target: DialogTarget = { kind: 'new' }
        return (
          <TaskSettingsModal
            isOpen={isLaunchModalOpen || gausiumLaunchMapId != null || gausiumLaunchExecTask != null}
            taskName=""
            onClose={() => {
              setIsLaunchModalOpen(false)
              setLaunchItem(undefined)
              setGausiumLaunchMapId(null)
              setGausiumLaunchExecTask(null)
            }}
            onSubmit={() => {
              setIsLaunchModalOpen(false)
              setLaunchItem(undefined)
              setGausiumLaunchMapId(null)
              setGausiumLaunchExecTask(null)
              fetchData(true)
            }}
            target={target}
            preselectedData={preselectedData}
            pickerScope={pickerScope}
            dateToEdit={null}
          />
        )
      })()}
    </div>
  )
}

export default ScenarioList
